import { useMutation, useQuery } from "@apollo/client";
import { makeStyles } from "@mui/styles";
import { format } from "date-fns";
import pluralize from "pluralize";
import { useState } from "react";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import CancelIcon from "@mui/icons-material/Cancel";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { displayVisitStatus } from "~/emr/utils/displayUtils";
import {
  CANCEL_REASON_OPTIONS,
  COLOR_KICK_BLUE,
  COLOR_KICK_LIGHT_BLUE,
  COLOR_KICK_LIGHT_TAN,
  COLOR_KICK_LIGHT_YELLOW,
  COLOR_KICK_NAVY,
  COLOR_KICK_SLATE,
  DB_STATUS_ACTIVE,
  DB_STATUS_CANCELED,
  DB_STATUS_PAUSED,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
  DOCTOR_VISIT_STATUS_READY,
  PAUSE_REASON_OPTIONS,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
} from "~/shared/sharedConstants";
import {
  displayFriendlyDate,
  isNullDate,
  subscriptionGrantsFollowUpAccess,
} from "~/shared/utils/sharedUtils";
import {
  clearActiveSubscriptionsBySubType,
  clearVisitCookies,
  setActiveSubscriptionBySubType,
} from "~/visits/utils/visitCookies";
import { setCookiesAndRedirectToInsomniaFollowUpVisit } from "~/visits/utils/visitUtils";
import {
  FILL_REQUEST_DEAD_STATUSES,
  subscriptionNextShipment,
  subscriptionPauseState,
  subscriptionShipmentStatus,
  subscriptionStatusChip,
} from "~/patient-portal/utils/treatmentCard";
import imgSleep from "~assets/images/visits/sleep-bottles.jpg";
import {
  CANCEL_SUBSCRIPTION,
  GET_PATIENT_RECORD,
  PAUSE_SUBSCRIPTION,
  RESUME_SUBSCRIPTION,
  SUBMIT_CANCELLATION_REASON,
  UNDO_CANCEL_SUBSCRIPTION,
} from "./portalQueries";

// pauseOptionFor returns the subscription's pause option for the given months, or null. The
// selectable pause durations and their exact resume dates come from the server
// (subscription.pauseOptions): each duration is measured from the NEXT billing date and is
// cycle-aware (monthly subs offer 1–3 months, quarterly only a full 3-month cycle skip), so the
// previewed resume date is exactly what the pause mutation will apply. An empty pauseOptions list
// means the subscription can't be paused and the pause UI is hidden.
function pauseOptionFor(subscription, months) {
  return (
    (subscription?.pauseOptions || []).find((opt) => opt.months === months) ||
    null
  );
}

// CANCEL_BENEFITS are the membership perks shown on the cancel-confirmation screen so the patient
// sees what they'd be giving up before they confirm. Sleep-specific, matching this (sleep-only)
// subscriptions page.
const CANCEL_BENEFITS = [
  "Up to 9 different sleep medications and future refills",
  "Working with your doctor to refine your personalized sleep plan",
  "Getting a good night's rest and waking up energized",
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
}));

const subscriptionItemStyle = {
  backgroundColor: "white",
  padding: "24px",
  width: "100%",
  margin: "0 0 16px",
  borderRadius: "12px",
  boxShadow:
    "0px 1px 2px rgba(30, 35, 71, 0.06), 0px 8px 24px rgba(30, 35, 71, 0.08)",
  maxWidth: "800px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

// Tone → pill color for the at-a-glance status chip. "danger" reuses the red already used
// elsewhere on the card; the rest are subtle status colors.
const STATUS_CHIP_COLORS = {
  warning: "#d69e2e",
  danger: "#e53e3e",
  neutral: "#718096",
};

// StatusChip renders the consolidated subscription status pill (Paused · resumes … /
// Cancels … / Canceled) beside the membership title, or nothing when there's no meaningful
// state — a plainly active subscription shows no chip.
function StatusChip({ subscription }) {
  const { label, tone } = subscriptionStatusChip(subscription);
  if (!label) {
    return null;
  }
  return (
    <span
      style={{
        color: "white",
        backgroundColor: STATUS_CHIP_COLORS[tone] || STATUS_CHIP_COLORS.neutral,
        fontSize: "13px",
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: "12px",
        whiteSpace: "nowrap",
        alignSelf: "center",
      }}
    >
      {label}
    </span>
  );
}

export function prescriptionWithFillRequests(prescription, fillRequests) {
  let refillsRemaining = prescription.refills - prescription.numRefillsFilled;
  refillsRemaining = Math.min(refillsRemaining, prescription.refills);
  const rxWithFill = {
    ...prescription,
    fillRequests: fillRequests.filter(
      (fillRequest) =>
        fillRequest.doctorPrescription1ID === prescription.id ||
        fillRequest.doctorPrescription2ID === prescription.id ||
        fillRequest.doctorPrescription3ID === prescription.id ||
        fillRequest.doctorPrescription4ID === prescription.id,
    ),
    refillsRemaining,
  };
  // Pick the most recent fill request that isn't canceled/failed — a canceled or
  // errored fill must never drive the "Last sent"/"Pending shipment" label or mask
  // an earlier real shipment. Mirrors the treatment card's dead-status handling.
  rxWithFill.mostRecentFillRequest = rxWithFill.fillRequests
    .filter(
      (fillRequest) => !FILL_REQUEST_DEAD_STATUSES.includes(fillRequest.status),
    )
    .reduce(
      (mostRecent, fillRequest) =>
        mostRecent.createdAt > fillRequest.createdAt ? mostRecent : fillRequest,
      { createdAt: 0 },
    );

  return rxWithFill;
}

function hasFirstPendingSubVisit(data) {
  if (!data?.patientRecord?.visits?.length) {
    return false;
  }
  // sort visits by visitDate having the most recent visit first
  const sortedVisits = [...data.patientRecord.visits].sort(
    (a, b) => new Date(b.visitDate) - new Date(a.visitDate),
  );
  for (let visit of sortedVisits) {
    if (
      visit.visitType === "IN" &&
      !displayVisitStatus(visit.status).includes("Closed")
    ) {
      return true;
    }
  }
}

function hasPendingFollowUpVisit(latestVisit) {
  return (
    latestVisit.visitType === VISIT_TYPE_INSOMNIA_FOLLOW_UP &&
    [
      DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
      DOCTOR_VISIT_STATUS_PENDING,
      DOCTOR_VISIT_STATUS_READY,
      DOCTOR_VISIT_STATUS_AWAITING_REPLY,
    ].includes(latestVisit.status)
  );
}

// Muted secondary text used throughout the card (quantities, directions, status lines).
const MUTED_TEXT_COLOR = "#718096";

// Darker companion to STATUS_CHIP_COLORS.warning, used for TEXT in warning treatments — the chip
// amber doesn't have enough contrast on light backgrounds at body sizes.
const WARNING_TEXT_COLOR = "#8a6d1d";

// Small uppercase label that gives the card a scannable section structure.
const sectionLabelStyle = {
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: COLOR_KICK_SLATE,
  marginBottom: "8px",
};

// quantityText renders a friendly "30 gummies" / "1 tablet" from the prescription's quantity and
// form factor (the old hardcoded "s" produced "30 Gummys" and "1 Tablets").
function quantityText(rxWithFill) {
  if (!rxWithFill.medicationFormFactor) {
    return `${rxWithFill.quantity}`;
  }
  return `${rxWithFill.quantity} ${pluralize(
    rxWithFill.medicationFormFactor.toLowerCase(),
    rxWithFill.quantity,
  )}`;
}

// MedicationRow is one prescription on the card: what it is and how much. Shipment status lives
// at the card level (one line per pharmacy fill request, since bundled meds ship together); only
// the out-of-refills note stays per-medication (autoship only, as before).
function MedicationRow({ rxWithFill, idx }) {
  const isAutoship = rxWithFill.autoshipStatus === DB_STATUS_ACTIVE;
  return (
    <div
      style={{
        padding: idx ? "14px 0" : "0 0 14px",
        borderTop: idx ? "1px solid #f0f0f0" : "none",
      }}
    >
      <div
        style={{ fontSize: "16px", fontWeight: 600, color: COLOR_KICK_NAVY }}
      >
        {rxWithFill.medicationName} {rxWithFill.medicationDosage}
      </div>
      <div
        style={{ fontSize: "14px", color: MUTED_TEXT_COLOR, marginTop: "2px" }}
      >
        {quantityText(rxWithFill)}
      </div>
      {isAutoship && !rxWithFill.refillsRemaining && (
        <div
          style={{
            fontSize: "14px",
            color: WARNING_TEXT_COLOR,
            marginTop: "8px",
          }}
        >
          No refills remaining. Please complete a follow-up visit for a new
          prescription.
        </div>
      )}
    </div>
  );
}

// NextShipmentStrip is the single most important answer on the card — when medication arrives
// next — so it gets its own tinted strip instead of blending into the text. Neutral tone is a
// date promise; warning means the prescription lapses first and a follow-up is needed.
function NextShipmentStrip({ shipment }) {
  const isWarning = shipment.tone === "warning";
  const Icon = isWarning ? WarningAmberRoundedIcon : LocalShippingOutlinedIcon;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        backgroundColor: isWarning
          ? COLOR_KICK_LIGHT_YELLOW
          : COLOR_KICK_LIGHT_BLUE,
        borderRadius: "8px",
        padding: "12px 14px",
        marginTop: "14px",
      }}
    >
      <Icon
        style={{
          color: isWarning ? STATUS_CHIP_COLORS.warning : COLOR_KICK_BLUE,
          fontSize: "20px",
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: "14px",
          fontWeight: 600,
          lineHeight: 1.45,
          color: isWarning ? WARNING_TEXT_COLOR : COLOR_KICK_NAVY,
        }}
      >
        {shipment.label}
      </span>
    </div>
  );
}

const PortalMySubscriptions = () => {
  const classes = useStyles();
  const { data } = useQuery(GET_PATIENT_RECORD);
  const [pauseSubscription] = useMutation(PAUSE_SUBSCRIPTION);
  const [resumeSubscription] = useMutation(RESUME_SUBSCRIPTION);
  const [cancelSubscription] = useMutation(CANCEL_SUBSCRIPTION);
  const [submitCancellationReason] = useMutation(SUBMIT_CANCELLATION_REASON);
  const [undoCancelSubscription] = useMutation(UNDO_CANCEL_SUBSCRIPTION);
  // Pause modal state: the subscription being paused (null = closed) and the chosen duration (months).
  const [pauseModalSub, setPauseModalSub] = useState(null);
  const [pauseMonths, setPauseMonths] = useState(1);
  // Required "why are you pausing?" capture (dropdown + always-shown free-text box), mirroring the
  // cancel reason UI; sent as the pause mutation's reasonCode / reasonDetail.
  const [pauseReasonCode, setPauseReasonCode] = useState("");
  const [pauseReasonDetail, setPauseReasonDetail] = useState("");
  // Click-time validation errors: set when "Pause subscription" is clicked without a reason (or
  // without detail for "other"). The button stays clickable so the click can explain what's missing
  // — a disabled button here looks enabled (its inline styles override MUI's disabled look).
  const [pauseReasonError, setPauseReasonError] = useState(false);
  const [pauseDetailError, setPauseDetailError] = useState(false);
  const [pauseSubmitting, setPauseSubmitting] = useState(false);
  // Cancel modal state: the subscription being canceled (null = closed) and a two-step flow —
  // "offer" (pause-instead) then "confirm" (benefits-you'll-miss + reason). Nothing is committed until
  // "Continue to cancelation" on the confirm step, which schedules the cancel and records the reason.
  const [cancelModalSub, setCancelModalSub] = useState(null);
  const [cancelStep, setCancelStep] = useState("offer");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [reasonCode, setReasonCode] = useState("");
  const [reasonDetail, setReasonDetail] = useState("");
  // Same click-time validation errors for the cancel confirm step's reason fields.
  const [cancelReasonError, setCancelReasonError] = useState(false);
  const [cancelDetailError, setCancelDetailError] = useState(false);
  // Card overflow ("⋮") menu state: the anchor element plus which subscription's card opened it,
  // since one shared Menu serves every card.
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSubscription, setMenuSubscription] = useState(null);
  const subscriptionsBySubType = {};

  clearActiveSubscriptionsBySubType();

  // create a map of subscriptions grouped by subType
  (data?.patientRecord?.subscriptions || []).forEach((subscription) => {
    // Prefer an active or paused subscription over a canceled one for the same subType.
    if (
      !subscriptionsBySubType[subscription.subType] ||
      subscription.status === DB_STATUS_ACTIVE ||
      subscription.status === DB_STATUS_PAUSED
    ) {
      subscriptionsBySubType[subscription.subType] = subscription;
      // Cache the subscription for the follow-up flow when it grants access (active, or paused but
      // within the paid-through window) so the follow-up is treated as covered, not a new purchase.
      if (subscriptionGrantsFollowUpAccess(subscription)) {
        setActiveSubscriptionBySubType(subscription.subType, subscription);
      }
    }
  });

  const openPauseModal = (subscription) => {
    // Default to the shortest offered pause — the most common choice.
    setPauseMonths(subscription.pauseOptions?.[0]?.months ?? 1);
    setPauseReasonCode("");
    setPauseReasonDetail("");
    setPauseReasonError(false);
    setPauseDetailError(false);
    setPauseModalSub(subscription);
  };

  const closePauseModal = () => {
    if (!pauseSubmitting) {
      setPauseModalSub(null);
    }
  };

  // doPause pauses the given subscription for `months`, optionally recording the reason. Shared by
  // the standalone Pause modal (which always passes a reasonCode plus any free-text detail) and the
  // "pause instead" option inside the Cancel modal (which passes neither).
  const doPause = async (subscription, months, reasonCode, reasonDetail) => {
    if (!subscription) {
      return;
    }
    setPauseSubmitting(true);
    try {
      // The server computes the resume date: next billing date + months, landing on a billing
      // boundary so billing (and shipments) resume the day the pause ends.
      const variables = {
        stripeSubscriptionID: subscription.stripeSubscriptionID,
        months,
      };
      if (reasonCode) {
        variables.reasonCode = reasonCode;
        variables.reasonDetail = reasonDetail || "";
      }
      const res = await pauseSubscription({ variables });
      if (res?.data?.pauseSubscription) {
        location.reload();
      } else {
        alert("Sorry, we couldn't pause your subscription. Please try again.");
        setPauseSubmitting(false);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
      setPauseSubmitting(false);
    }
  };

  // Validate, then send the required reason code plus whatever the patient typed in the
  // always-shown free-text box. A missing reason (or missing detail for "other") shows an inline
  // error under the offending field instead of pausing.
  const submitPause = () => {
    const missingReason = !pauseReasonCode;
    const missingDetail =
      pauseReasonCode === "other" && !pauseReasonDetail.trim();
    setPauseReasonError(missingReason);
    setPauseDetailError(missingDetail);
    if (missingReason || missingDetail) {
      return;
    }
    doPause(pauseModalSub, pauseMonths, pauseReasonCode, pauseReasonDetail.trim());
  };

  const isPendingCancel = (subscription) =>
    !isNullDate(subscription.cancelAt) &&
    new Date(subscription.cancelAt) > Date.now();

  const openCardMenu = (event, subscription) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSubscription(subscription);
  };

  const closeCardMenu = () => {
    setMenuAnchorEl(null);
    setMenuSubscription(null);
  };

  const openCancelModal = (subscription) => {
    setPauseMonths(subscription.pauseOptions?.[0]?.months ?? 1);
    setReasonCode("");
    setReasonDetail("");
    setCancelReasonError(false);
    setCancelDetailError(false);
    // The "pause instead" offer only makes sense when the subscription has pause options; without
    // any (e.g. semiannual billing) go straight to the cancel confirmation.
    setCancelStep(subscription.pauseOptions?.length ? "offer" : "confirm");
    setCancelModalSub(subscription);
  };

  const closeCancelModal = () => {
    // Nothing is committed until "Continue to cancelation" (which reloads the page on success), so
    // dismissing at the offer or confirm step simply abandons the flow with no side effects. Only
    // block the close while a pause or cancel is mid-flight.
    if (cancelSubmitting || pauseSubmitting) {
      return;
    }
    setCancelModalSub(null);
  };

  // confirmCancel commits the end-of-period cancellation from the confirm screen, then best-effort
  // records the reason the patient selected before reloading so the card reflects the
  // now-scheduled cancellation. Because the reason is captured up front, it's sent right after the
  // cancel rather than on a separate post-cancel screen.
  const confirmCancel = async () => {
    if (!cancelModalSub) {
      return;
    }
    // A reason is required (with detail for "other"); flag the offending field inline instead of
    // canceling without one.
    const missingReason = !reasonCode;
    const missingDetail = reasonCode === "other" && !reasonDetail.trim();
    setCancelReasonError(missingReason);
    setCancelDetailError(missingDetail);
    if (missingReason || missingDetail) {
      return;
    }
    setCancelSubmitting(true);
    try {
      const res = await cancelSubscription({
        variables: {
          stripeSubscriptionID: cancelModalSub.stripeSubscriptionID,
        },
      });
      if (!res?.data?.cancelSubscription) {
        alert("Sorry, we couldn't cancel your subscription. Please try again.");
        setCancelSubmitting(false);
        return;
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
      setCancelSubmitting(false);
      return;
    }
    // Cancel committed. Record the reason (validation above guarantees one) — best-effort, never
    // block the cancel.
    if (reasonCode) {
      try {
        await submitCancellationReason({
          variables: {
            stripeSubscriptionID: cancelModalSub.stripeSubscriptionID,
            reasonCode,
            reasonDetail: reasonDetail.trim(),
          },
        });
      } catch (error) {
        // The cancel already went through, so never block on a failed reason submit.
        console.error("Failed to submit cancellation reason", error);
      }
    }
    location.reload();
  };

  const handleUndoCancel = async (subscription) => {
    if (
      !confirm(
        "Keep your subscription? This will stop the scheduled cancellation and your membership will continue.",
      )
    ) {
      return;
    }
    try {
      const res = await undoCancelSubscription({
        variables: { stripeSubscriptionID: subscription.stripeSubscriptionID },
      });
      if (res?.data?.undoCancelSubscription) {
        location.reload();
      } else {
        alert("Sorry, we couldn't update your subscription. Please try again.");
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const handleResumeSubscription = async (subscription) => {
    // Backed by the same resume mutation in both states, framed by what it does for the
    // patient: while the pause is only scheduled (still inside the paid period) resuming just
    // cancels the upcoming pause — no charge, original billing date kept. Once the pause is
    // functionally in effect, resuming charges immediately to restart.
    const scheduled = subscriptionPauseState(subscription) === "scheduled";
    const confirmMessage = scheduled
      ? `Cancel your scheduled pause? You won't be charged — your subscription continues and your next charge stays on ${displayFriendlyDate(
          subscription.paidThroughAt,
        )}.`
      : "Resume your subscription now? You'll be charged today to restart and your medication will ship.";
    if (!confirm(confirmMessage)) {
      return;
    }
    try {
      const res = await resumeSubscription({
        variables: { stripeSubscriptionID: subscription.stripeSubscriptionID },
      });
      if (res?.data?.resumeSubscription) {
        alert(
          scheduled
            ? "Your scheduled pause has been canceled."
            : "Your subscription has been resumed.",
        );
        location.reload();
      } else {
        alert("Sorry, we couldn't resume your subscription. Please try again.");
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  return (
    <main className={classes.content}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
          My Subscriptions
        </h3>
        {Object.values(subscriptionsBySubType).map((subscription) => {
          if (!subscription.prescriptions) {
            return null;
          }
          const hasRefillsRemaining = subscription.prescriptions.some(
            (rx) => rx.numRefillsFilled < rx.refills,
          );
          const hasAutoship = subscription.prescriptions.some(
            (rx) => rx.autoshipStatus === DB_STATUS_ACTIVE,
          );
          const hasPrescriptions = subscription.prescriptions.length > 0;
          // The "⋮" menu currently only holds "Cancel subscription", so it only shows when
          // canceling is possible: an active subscription that isn't already scheduled to cancel.
          const showCardMenu =
            hasPrescriptions &&
            subscription.status === DB_STATUS_ACTIVE &&
            !isPendingCancel(subscription);
          // Pause is offered only for an active, not-pending-cancel subscription the server says
          // can pause (pauseOptions non-empty); it renders under the shipment info.
          const showPauseCTA =
            showCardMenu && subscription.pauseOptions?.length > 0;
          // Card-level next-shipment line (pause- and rx-expiration-aware); null when there's
          // nothing truthful to promise. Replaces the old per-medication "Next shipment" rows,
          // which all carried the same subscription-wide date anyway.
          const nextShipment = subscriptionNextShipment(subscription);
          const rxsWithFill = subscription.prescriptions.map((prescription) =>
            prescriptionWithFillRequests(prescription, subscription.fillRequests),
          );
          // Shipment status, deduped by fill request: meds bundled into one pharmacy fill
          // request share a single "Shipped … · Track shipment" line instead of repeating it per
          // medication; distinct fill requests (rare) each keep their own line. Visit-only
          // non-autoship orders have no shipment to report (same gate the per-med lines used).
          const seenShipments = new Set();
          const shipmentLines = rxsWithFill
            .filter(
              (rx) =>
                rx.autoshipStatus === DB_STATUS_ACTIVE ||
                !subscription.latestVisit?.orderType?.includes("visit-only"),
            )
            .map((rx) => subscriptionShipmentStatus(rx))
            .filter((shipment) => {
              if (!shipment.label) {
                return false;
              }
              const key = `${shipment.label}|${shipment.trackingURL}`;
              if (seenShipments.has(key)) {
                return false;
              }
              seenShipments.add(key);
              return true;
            });
          // The action panel's contextual one-liner (same conditions as the old centered copy,
          // computed up front so an empty message renders nothing).
          const actionMessage =
            subscription.status === DB_STATUS_CANCELED
              ? "Your subscription has been canceled. Complete a follow-up visit to restart your subscription."
              : hasPendingFollowUpVisit(subscription.latestVisit)
                ? "Your follow-up visit is currently under review by one of our doctors."
                : hasRefillsRemaining && hasPrescriptions
                  ? hasAutoship
                    ? "Want to update something or try a new medication? Your subscription allows for unlimited follow-up visits."
                    : "How is your medication trial going?\nComplete a follow-up visit any time to adjust medication or set up automatic refill shipments."
                  : "";

          return (
            <div
              key={subscription.id}
              style={{
                backgroundColor: "white",
                padding: "24px",
                width: "100%",
                margin: "0 0 16px",
                borderRadius: "12px",
                boxShadow:
                  "0px 1px 2px rgba(30, 35, 71, 0.06), 0px 8px 24px rgba(30, 35, 71, 0.08)",
                maxWidth: "800px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #eee",
                  paddingBottom: "16px",
                  marginBottom: "20px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <img
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "8px",
                      marginRight: "14px",
                      objectFit: "cover",
                    }}
                    src={imgSleep}
                    alt=""
                  />
                  <h3
                    style={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: COLOR_KICK_NAVY,
                      margin: "0",
                    }}
                  >
                    Sleep Membership
                  </h3>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <StatusChip subscription={subscription} />
                  {showCardMenu && (
                    <IconButton
                      aria-label="Subscription options"
                      aria-haspopup="true"
                      size="small"
                      style={{ marginRight: "-8px" }}
                      onClick={(event) => openCardMenu(event, subscription)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </div>
              </div>

              {!hasPrescriptions && (
                <div
                  style={{
                    fontSize: "15px",
                    color: MUTED_TEXT_COLOR,
                    padding: "4px 0 8px",
                  }}
                >
                  Your visit will be reviewed by one of our doctors soon.
                </div>
              )}

              {hasPrescriptions && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
                  {/* Left: what the patient is taking and when it arrives. */}
                  <div style={{ flex: "1 1 300px", minWidth: 0 }}>
                    <div style={sectionLabelStyle}>
                      {subscription.prescriptions.length === 1
                        ? "Your medication"
                        : "Your medications"}
                    </div>
                    {rxsWithFill.map((rxWithFill, idx) => (
                      <MedicationRow
                        key={rxWithFill.id}
                        rxWithFill={rxWithFill}
                        idx={idx}
                      />
                    ))}
                    {shipmentLines.length > 0 && (
                      <div
                        style={{
                          borderTop: "1px solid #f0f0f0",
                          paddingTop: "14px",
                        }}
                      >
                        <div style={sectionLabelStyle}>Shipment status</div>
                        {shipmentLines.map((shipment) => (
                          <div
                            key={`${shipment.label}|${shipment.trackingURL}`}
                            style={{
                              fontSize: "14px",
                              color: MUTED_TEXT_COLOR,
                              marginBottom: "4px",
                            }}
                          >
                            {shipment.label}
                            {shipment.trackingURL && (
                              <>
                                {" · "}
                                <a
                                  href={shipment.trackingURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: COLOR_KICK_BLUE,
                                    fontWeight: 600,
                                  }}
                                >
                                  Track shipment
                                </a>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {nextShipment && (
                      <NextShipmentStrip shipment={nextShipment} />
                    )}
                    {showPauseCTA && (
                      <div style={{ marginTop: "10px" }}>
                        <Button
                          size="small"
                          style={{
                            color: COLOR_KICK_SLATE,
                            textTransform: "none",
                            // Cancel the button's built-in padding so the label lines up with
                            // the med text above.
                            marginLeft: "-5px",
                          }}
                          onClick={() => openPauseModal(subscription)}
                        >
                          Pause subscription
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Right: what the patient can do about it. */}
                  <div
                    style={{
                      flex: "1 1 280px",
                      backgroundColor: COLOR_KICK_LIGHT_TAN,
                      borderRadius: "10px",
                      padding: "20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    {actionMessage && (
                      <div
                        style={{
                          maxWidth: "320px",
                          fontSize: "14px",
                          color: MUTED_TEXT_COLOR,
                          lineHeight: 1.5,
                          marginBottom: "14px",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {actionMessage}
                      </div>
                    )}
                    <Button
                      color="primary"
                      size="large"
                      style={{
                        backgroundColor: COLOR_KICK_BLUE,
                        color: "white",
                        width: "100%",
                        maxWidth: "300px",
                        borderRadius: "8px",
                        fontWeight: 400,
                      }}
                      onClick={async () => {
                        // A scheduled pause (still inside the paid period) doesn't gate
                        // follow-ups: the visit is covered by the last paid invoice and the
                        // pause stays in place, so it flows exactly like an active membership.
                        // Only a functionally paused membership requires confirming the
                        // resume-and-charge restart.
                        if (
                          subscriptionPauseState(subscription) === "paused" &&
                          !confirm(
                            "Completing a follow-up visit will resume your subscription and you'll be charged to restart it. Proceed?",
                          )
                        ) {
                          return;
                        }
                        // TODO: dont hardcode to insomnia
                        setCookiesAndRedirectToInsomniaFollowUpVisit(
                          data.patientRecord.patient,
                          subscription.latestVisit,
                        );
                      }}
                    >
                      Follow-up Visit&nbsp;
                      <NavigateNextIcon />
                    </Button>
                    {isPendingCancel(subscription) ? (
                      <div style={{ marginTop: "12px" }}>
                        <div
                          style={{
                            fontSize: "14px",
                            color: MUTED_TEXT_COLOR,
                            marginBottom: "2px",
                          }}
                        >
                          Your membership will cancel on{" "}
                          {displayFriendlyDate(subscription.cancelAt)}.
                        </div>
                        <Button
                          size="small"
                          style={{
                            color: COLOR_KICK_BLUE,
                            textTransform: "none",
                          }}
                          onClick={() => handleUndoCancel(subscription)}
                        >
                          Keep my subscription
                        </Button>
                      </div>
                    ) : subscriptionPauseState(subscription) === "scheduled" ? (
                      // Pause requested but billing hasn't stopped yet: spell out when it
                      // takes effect and offer to undo it (a no-charge resume that keeps the
                      // original billing date), mirroring the pending-cancel block above.
                      <div style={{ marginTop: "12px" }}>
                        <div
                          style={{
                            fontSize: "14px",
                            color: MUTED_TEXT_COLOR,
                            marginBottom: "2px",
                          }}
                        >
                          Your subscription will pause on{" "}
                          {displayFriendlyDate(subscription.paidThroughAt)}
                          {!isNullDate(subscription.pausedUntil) && (
                            <>
                              {" "}
                              and resume{" "}
                              {displayFriendlyDate(subscription.pausedUntil)}
                            </>
                          )}
                          .
                        </div>
                        <Button
                          size="small"
                          style={{
                            color: COLOR_KICK_BLUE,
                            textTransform: "none",
                          }}
                          onClick={() => handleResumeSubscription(subscription)}
                        >
                          Cancel scheduled pause
                        </Button>
                      </div>
                    ) : (
                      subscription.status === DB_STATUS_PAUSED && (
                        <Button
                          size="small"
                          style={{
                            color: COLOR_KICK_BLUE,
                            textTransform: "none",
                            marginTop: "10px",
                          }}
                          onClick={() => handleResumeSubscription(subscription)}
                        >
                          Resume subscription now
                        </Button>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {hasFirstPendingSubVisit(data) && (
          <div
            style={{
              ...subscriptionItemStyle,
            }}
          >
            <h5>
              Thank you for completing an insomnia visit. It is currently under
              review by one our doctors.
            </h5>
          </div>
        )}

        {data &&
          !data.patientRecord?.subscriptions?.length &&
          !hasFirstPendingSubVisit(data) && (
            <div
              style={{
                ...subscriptionItemStyle,
              }}
            >
              <h5>No subscriptions at the moment</h5>
              <Button
                onClick={() => {
                  clearVisitCookies();
                  window.location.href = `/visits/inc/1`;
                }}
                color="primary"
                size="large"
                style={{
                  backgroundColor: COLOR_KICK_BLUE,
                  color: "white",
                  width: "300px",
                  margin: "20px 0",
                }}
              >
                Start Insomnia Visit&nbsp;
                <NavigateNextIcon />
              </Button>
            </div>
          )}
      </div>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeCardMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            // Grab the subscription before closing, since closing clears it.
            const subscription = menuSubscription;
            closeCardMenu();
            openCancelModal(subscription);
          }}
        >
          Cancel subscription
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(pauseModalSub)}
        onClose={closePauseModal}
        aria-labelledby="pause-subscription-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="pause-subscription-dialog-title">
          Pause your subscription
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: "20px" }}>
            We&apos;ll pause billing and shipments until your subscription
            automatically resumes. You can resume sooner any time.
          </DialogContentText>
          <TextField
            select
            fullWidth
            label="Pause duration"
            value={pauseMonths}
            onChange={(e) => setPauseMonths(Number(e.target.value))}
          >
            {(pauseModalSub?.pauseOptions || []).map((opt) => (
              <MenuItem key={opt.months} value={opt.months}>
                {opt.months} {opt.months === 1 ? "month" : "months"}
              </MenuItem>
            ))}
          </TextField>
          {pauseOptionFor(pauseModalSub, pauseMonths) && (
            <DialogContentText style={{ marginTop: "12px", fontSize: "14px" }}>
              Your subscription will automatically resume on{" "}
              {format(
                new Date(pauseOptionFor(pauseModalSub, pauseMonths).resumesAt),
                "EEEE, MMMM do, yyyy",
              )}
              .
            </DialogContentText>
          )}
          <DialogContentText
            style={{
              marginTop: "20px",
              marginBottom: "12px",
              fontSize: "14px",
            }}
          >
            What&apos;s the main reason for pausing?
          </DialogContentText>
          <TextField
            select
            fullWidth
            label="Reason"
            value={pauseReasonCode}
            onChange={(e) => {
              setPauseReasonCode(e.target.value);
              setPauseReasonError(false);
              if (e.target.value !== "other") {
                setPauseDetailError(false);
              }
            }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            error={pauseReasonError}
            helperText={pauseReasonError ? "Please select a reason." : ""}
          >
            <MenuItem value="" disabled>
              Choose an option
            </MenuItem>
            {PAUSE_REASON_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="Tell us more"
            value={pauseReasonDetail}
            onChange={(e) => {
              setPauseReasonDetail(e.target.value);
              setPauseDetailError(false);
            }}
            error={pauseDetailError}
            helperText={pauseDetailError ? "Please tell us a bit more." : ""}
            style={{ marginTop: "12px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closePauseModal}
            color="primary"
            disabled={pauseSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={submitPause}
            variant="contained"
            style={{ backgroundColor: COLOR_KICK_BLUE, color: "white" }}
            disabled={pauseSubmitting}
          >
            {pauseSubmitting ? "Pausing…" : "Pause subscription"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(cancelModalSub)}
        onClose={closeCancelModal}
        aria-labelledby="cancel-subscription-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        {cancelStep === "offer" ? (
          <>
            <DialogTitle id="cancel-subscription-dialog-title">
              Would you like to pause instead?
            </DialogTitle>
            <DialogContent>
              <DialogContentText style={{ marginBottom: "20px" }}>
                Pausing keeps your membership — we&apos;ll hold billing and
                shipments until it automatically resumes, and you can resume
                sooner any time.
              </DialogContentText>
              <TextField
                select
                fullWidth
                label="Pause duration"
                value={pauseMonths}
                onChange={(e) => setPauseMonths(Number(e.target.value))}
              >
                {(cancelModalSub?.pauseOptions || []).map((opt) => (
                  <MenuItem key={opt.months} value={opt.months}>
                    {opt.months} {opt.months === 1 ? "month" : "months"}
                  </MenuItem>
                ))}
              </TextField>
              {pauseOptionFor(cancelModalSub, pauseMonths) && (
                <DialogContentText
                  style={{ marginTop: "12px", fontSize: "14px" }}
                >
                  Your subscription will automatically resume on{" "}
                  {format(
                    new Date(
                      pauseOptionFor(cancelModalSub, pauseMonths).resumesAt,
                    ),
                    "EEEE, MMMM do, yyyy",
                  )}
                  .
                </DialogContentText>
              )}
            </DialogContent>
            <DialogActions
              style={{
                flexDirection: "column",
                alignItems: "stretch",
                padding: "8px 24px 20px",
              }}
            >
              <Button
                onClick={() => doPause(cancelModalSub, pauseMonths)}
                variant="contained"
                style={{
                  backgroundColor: COLOR_KICK_BLUE,
                  color: "white",
                  marginBottom: "8px",
                }}
                disabled={pauseSubmitting}
              >
                {pauseSubmitting ? "Pausing…" : "Pause subscription"}
              </Button>
              <Button
                onClick={() => setCancelStep("confirm")}
                color="primary"
                disabled={pauseSubmitting}
              >
                Cancel my subscription
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle id="cancel-subscription-dialog-title">
              Cancel your subscription
            </DialogTitle>
            <DialogContent>
              <DialogContentText
                style={{ fontSize: "14px", marginBottom: "6px" }}
              >
                Selected subscription
              </DialogContentText>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "14px 16px",
                  marginBottom: "24px",
                }}
              >
                <EventRepeatIcon
                  style={{ color: COLOR_KICK_BLUE, marginRight: "12px" }}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>Sleep Membership</div>
                  {cancelModalSub?.prescriptions?.length > 0 && (
                    <div style={{ fontSize: "13px", color: "#718096" }}>
                      {cancelModalSub.prescriptions
                        .map(
                          (rx) => `${rx.medicationName} ${rx.medicationDosage}`,
                        )
                        .join(", ")}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ fontWeight: 700, marginBottom: "12px" }}>
                Benefits you will be missing out on…
              </div>
              {CANCEL_BENEFITS.map((benefit) => (
                <div
                  key={benefit}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: "10px",
                  }}
                >
                  <CancelIcon
                    style={{
                      color: STATUS_CHIP_COLORS.danger,
                      marginRight: "10px",
                      fontSize: "22px",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: "15px" }}>{benefit}</span>
                </div>
              ))}

              <div
                style={{
                  backgroundColor: COLOR_KICK_LIGHT_BLUE,
                  borderRadius: "8px",
                  padding: "16px",
                  marginTop: "20px",
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: "12px" }}>
                  Could you tell us why you&apos;re leaving?
                </div>
                <TextField
                  select
                  fullWidth
                  label="Reason"
                  value={reasonCode}
                  onChange={(e) => {
                    setReasonCode(e.target.value);
                    setCancelReasonError(false);
                    if (e.target.value !== "other") {
                      setCancelDetailError(false);
                    }
                  }}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  error={cancelReasonError}
                  helperText={
                    cancelReasonError ? "Please select a reason." : ""
                  }
                  style={{ backgroundColor: "white" }}
                >
                  <MenuItem value="" disabled>
                    Choose an option
                  </MenuItem>
                  {CANCEL_REASON_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Tell us more"
                  value={reasonDetail}
                  onChange={(e) => {
                    setReasonDetail(e.target.value);
                    setCancelDetailError(false);
                  }}
                  error={cancelDetailError}
                  helperText={
                    cancelDetailError ? "Please tell us a bit more." : ""
                  }
                  style={{ marginTop: "12px", backgroundColor: "white" }}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCancelStep("offer")}
                color="primary"
                disabled={cancelSubmitting}
              >
                Go back
              </Button>
              <Button
                onClick={confirmCancel}
                variant="contained"
                style={{ backgroundColor: COLOR_KICK_BLUE, color: "white" }}
                disabled={cancelSubmitting}
              >
                {cancelSubmitting ? "Canceling…" : "Continue to cancelation"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </main>
  );
};

export default PortalMySubscriptions;
